/* eslint-disable @typescript-eslint/no-explicit-any */
import { Property, Patient, CoreAvatar } from "../../types";
import { useState, useCallback } from "react";
import {
  Card,
  Text,
  TextField,
  BlockStack,
  Checkbox,
  InlineStack,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import EditPropertiesRenderer from "./EditPropertiesRenderer";

interface PropertiesTabContentProps {
  properties: Property[];
  patient?: Patient;
  loading?: boolean;
  onPropertyChange?: (propertyName: string, value: any) => void;
  disabled?: boolean;
  updatedValues?: Partial<Patient>;
  onFileUpload?: (propertyName: string, file: File) => void;
  onFileRemove?: (propertyName: string) => void;
  validationErrors?: Record<string, string>;
}

const propertiesToHide = [
  "core_avatar",
  "core_private_note",
  "core_private_note_tone",
  "core_patient_owner",
];

function PropertiesTabContent({
  properties,
  patient,
  loading,
  onPropertyChange,
  disabled,
  updatedValues = {},
  onFileUpload,
  onFileRemove,
  validationErrors = {},
}: PropertiesTabContentProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyShortlisted, setOnlyShortlisted] = useState(false);
  const [hideBlankProperties, setHideBlankProperties] = useState(false);
  const [files, setFiles] = useState<
    { propertyName: string; file: File | null; url?: string }[]
  >([]);

  const handleSearchChange = useCallback(
    (value: string) => setSearchQuery(value),
    []
  );

  const filteredProperties = properties
    .filter((property) => {
      // First check if property matches shortlisted filter
      if (onlyShortlisted && !property.isShortlisted) {
        return false;
      }

      // Then check if property matches blank properties filter
      if (
        hideBlankProperties &&
        (patient?.[property.name] === undefined ||
          patient?.[property.name] === null ||
          patient?.[property.name] === "")
      ) {
        return false;
      }

      // Finally apply search filter
      return (
        property.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .sort((a, b) => {
      // Sort alphabetically by label
      return a.label.localeCompare(b.label);
    });

  const handleChange = useCallback(
    (propertyName: string, value: any) => {
      onPropertyChange?.(propertyName, value);
    },
    [onPropertyChange]
  );

  const handleDropZoneDrop = useCallback(
    (propertyName: string, acceptedFiles: File[]) => {
      setFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(
          (f) => f.propertyName !== propertyName
        );
        updatedFiles.push({
          propertyName,
          file: acceptedFiles[0],
          url: undefined,
        });
        return updatedFiles;
      });

      if (acceptedFiles[0] && onFileUpload) {
        onFileUpload(propertyName, acceptedFiles[0]);
      }
    },
    [onFileUpload]
  );

  const handleRemoveFile = useCallback(
    (propertyName: string) => {
      setFiles((prevFiles) =>
        prevFiles.filter((f) => f.propertyName !== propertyName)
      );

      if (onFileRemove) {
        onFileRemove(propertyName);
      }
    },
    [onFileRemove]
  );

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingLg" as="h3">
          {t("patientDetails.allProperties")}
        </Text>

        <TextField
          label={t("patientDetails.searchProperties")}
          value={searchQuery}
          onChange={handleSearchChange}
          autoComplete="off"
          placeholder={t("patientDetails.searchPropertiesPlaceholder")}
          labelHidden
        />

        <InlineStack gap="300">
          <Checkbox
            label={t("patientDetails.onlyShortlisted")}
            checked={onlyShortlisted}
            onChange={setOnlyShortlisted}
          />
          <Checkbox
            label={t("patientDetails.hideBlankProperties")}
            checked={hideBlankProperties}
            onChange={setHideBlankProperties}
          />
        </InlineStack>

        {loading ? (
          <SkeletonBodyText lines={10} />
        ) : (
          <BlockStack gap="400">
            {filteredProperties
              .filter((property) => !propertiesToHide.includes(property.name))
              .map((property) => {
                const avatarValue = patient?.[
                  property.name
                ] as unknown as CoreAvatar;
                const fileObj = files.find(
                  (f) => f.propertyName === property.name
                ) ?? {
                  file: undefined,
                  url: avatarValue?.value,
                };
                return (
                  <EditPropertiesRenderer
                    key={property.name}
                    id={property.renderer?.id ?? "textInput"}
                    label={property.label}
                    value={
                      updatedValues[property.name] ?? patient?.[property.name]
                    }
                    onChange={(value) => handleChange(property.name, value)}
                    disabled={disabled || property.isReadOnlyValue}
                    helpText={property.description}
                    isRequired={property.isRequired}
                    file={fileObj.file || undefined}
                    imageUrl={fileObj.url}
                    onDrop={(acceptedFiles) =>
                      handleDropZoneDrop(property.name, acceptedFiles)
                    }
                    comboOptions={property.options ?? []}
                    onRemoveFile={() => handleRemoveFile(property.name)}
                    renderer={property.renderer}
                    patientId={patient?.id}
                    error={validationErrors[property.name]}
                  />
                );
              })}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default PropertiesTabContent;
